import type {UniqueIdentifier} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';

import type {FlattenedItem, ShallowFlattenedItem, TreeItem, TreeItems} from './types';

import { v4 as uuid } from 'uuid'
import { getAuth } from 'firebase/auth';

export const iOS = /iPad|iPhone|iPod/.test(navigator.platform);

function getDragDepth(offset: number, indentationWidth: number) {
  return Math.round(offset / indentationWidth);
}

export function getProjection(
  items: FlattenedItem[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier,
  dragOffset: number,
  indentationWidth: number
) {
  const overItemIndex = items.findIndex(({id}) => id === overId);
  const activeItemIndex = items.findIndex(({id}) => id === activeId);
  const activeItem = items[activeItemIndex];
  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];
  const dragDepth = getDragDepth(dragOffset, indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;
  const maxDepth = getMaxDepth({
    previousItem,
  });
  const minDepth = getMinDepth({nextItem});
  let depth = projectedDepth;

  if (projectedDepth >= maxDepth) {
    depth = maxDepth;
  } else if (projectedDepth < minDepth) {
    depth = minDepth;
  }

  return {depth, maxDepth, minDepth, parentId: getParentId()};

  function getParentId() {
    if (depth === 0 || !previousItem) {
      return null;
    }

    if (depth === previousItem.depth) {
      return previousItem.parentId;
    }

    if (depth > previousItem.depth) {
      return previousItem.id;
    }

    const newParent = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find((item) => item.depth === depth)?.parentId;

    return newParent ?? null;
  }
}

function getMaxDepth({previousItem}: {previousItem: FlattenedItem}) {
  if (previousItem) {
    return previousItem.depth + 1;
  }

  return 0;
}

function getMinDepth({nextItem}: {nextItem: FlattenedItem}) {
  if (nextItem) {
    return nextItem.depth;
  }

  return 0;
}

function flattenFast(
  items: TreeItems,
  // parentId: UniqueIdentifier | null = null,
  startDepth = 0
): FlattenedItem[] {
  // Guard clause if your data hasn't finished hydrating from IndexedDB yet
  if (!items ) return [];

  const flatList: FlattenedItem[] = [];
  
  // Create our LIFO stack array using your top-level root IDs
  // We reverse it so the first root node is popped and processed first
  const stack = [...items].reverse();
  const parentIdStack: (UniqueIdentifier | null)[] = [];
  const depthStack: number[] = [];
  let index = 0;

  while (stack.length > 0) {
    const item = stack.pop();
    const parentId = parentIdStack.pop() ?? null; // Safe fallback for missing metadata
    const depth = depthStack.pop() ?? startDepth;
    
    // Support both native JavaScript Maps or plain Object dictionaries dynamically
    // const item = typeof itemsMap.get === 'function' ? itemsMap.get(itemId) : itemsMap[itemId];
    
    if(!item) continue;

    // let parentId = parentIdStack.pop();

    // if(!parentId) {
    //   parentId = null;
    // }

    // let depth = depthStack.pop();

    // if(!depth) {
    //   depth = startDepth;
    // }

    // 1. Push the visible item straight into our flat rendering array
    flatList.push({...item, parentId, depth, index});
    index++

    // 2. THE SENIOR SHORT-CIRCUIT:
    // Check if the node is expanded. Because the collapse state is stored right on the object, 
    // we just check your boolean property (adjust the key name if it's 'isCollapsed' or 'collapsed')
    const isExpanded = !item.collapsed; 
    
    if (isExpanded && item.children && item.children.length > 0) {
      // Reverse children when pushing to maintain the perfect visual top-down order
      for (let i = item.children.length - 1; i >= 0; i--) {
        parentIdStack.push(item.id);
        depthStack.push(depth+1);
        stack.push(item.children[i]);
      }
    }
    // If it's collapsed, the loop skips the children completely. 
    // Those hidden sub-nodes cost exactly 0ms of CPU time!
  }
  return flatList;
};

function flatten(
  items: TreeItems,
  parentId: UniqueIdentifier | null = null,
  depth = 0
): FlattenedItem[] {
  //TODO: This funciton gets called a lot so obviating this guard would be good.
  if((items !== null) && (items !== undefined)) {
      return items.reduce<FlattenedItem[]>((acc, item, index) => {

        if(item.collapsed) {
          return [
            ...acc,
            {...item, parentId, depth, index},
            ...[],
          ];
        }
        else {
          return [
            ...acc,
            {...item, parentId, depth, index},
            ...flatten(item.children, item.id, depth + 1),
          ];
        };
      }, 
      []);

  }
  else {
    return [];
  }
}

export function flattenTree(items: TreeItems): FlattenedItem[] {
  return flattenFast(items);
}

function flattenAll(
  items: TreeItems,
  parentId: UniqueIdentifier | null = null,
  depth = 0
): FlattenedItem[] {
  //TODO: This funciton gets called a lot so obviating this guard would be good.
  if((items !== null) && (items !== undefined)) {
      return items.reduce<FlattenedItem[]>((acc, item, index) => {
        return [
          ...acc,
          {...item, parentId, depth, index},
          ...flattenAll(item.children, item.id, depth + 1),
        ];
      }, 
      []);

  }
  else {
    return [];
  }
}

export function flattenTreeAll(items: TreeItems): FlattenedItem[] {
  return flattenAll(items);
}

function shallowFlatten(
  items: TreeItems,
  parentId: UniqueIdentifier | null = null,
  depth = 0
): ShallowFlattenedItem[] {
  //TODO: This funciton gets called a lot so obviating this guard would be good.
  if((items !== null) && (items !== undefined)) {
      return items.reduce<ShallowFlattenedItem[]>((acc, item, index) => {

        const currentItemchildren: TreeItems = item.children;
        item.children = [];
        const childrenIds: UniqueIdentifier[] = getIdsFromObjects(currentItemchildren);

        return [
          ...acc,
          {...item, parentId, depth, index, childrenIds},
          ...shallowFlatten(currentItemchildren, item.id, depth + 1), //pass in ids ere
        ];
      }, 
      []);

  }
  else {
    return [];
  }
}

export function shallowFlattenTree(items: TreeItems): ShallowFlattenedItem[] {
  return shallowFlatten(items);
}

export function buildTree(flattenedItems: FlattenedItem[]): TreeItems {
  const root: TreeItem = {id: 'root', children: []};
  const nodes: Record<string, TreeItem> = {[root.id]: root};
  const items = flattenedItems.map((item) => ({...item, children: []}));

  for (const item of items) {
    const {id, children} = item;
    const parentId = item.parentId ?? root.id;
    const parent = nodes[parentId] ?? findItem(items, parentId);

    nodes[id] = {id, children};
    parent.children.push(item);
  }

  return root.children;
}

function getObjectsFromIds(items: TreeItems, childrenIds: UniqueIdentifier[]): TreeItems {
  const children: TreeItems = [];

  for (const childId of childrenIds) {
    const child = findItem(items, childId);

    if(child) {
      children.push(child);
    }
  }

  return children;
}

function getIdsFromObjects(items: TreeItems): UniqueIdentifier[] {
  const childrenIds: UniqueIdentifier[] = [];

  for (const child of items) {
      childrenIds.push(child['id']);
  }

  return childrenIds;
}

export function buildTreeFromShallowItems(shallowFlattenedItems: ShallowFlattenedItem[]): TreeItems {
  const root: TreeItem = {id: 'root', children: []};
  const nodes: Record<string, TreeItem> = {[root.id]: root};
  const items: ShallowFlattenedItem[] = shallowFlattenedItems.map((item) => ({...item, children: []}));

  for (const item of items) {
    const children = getObjectsFromIds(items, item.childrenIds);// yeah maybe this falls out of scope too soon...
    item.children = children; // FIXME:: Really shouldn't need this... See below...
    const {id} = item;
    const parentId = item.parentId ?? root.id;
    const parent = nodes[parentId] ?? findItem(items, parentId);

    nodes[id] = {id, children};

    // FIXME:: Really not sure why i need to check for/guard against duplicates here but it makes it work...
    //         I suspect some kind of object duplication/cloning/Object falling out of scope/deep copy
    //         in the stack somewhere...
    if(!findItem(parent.children, item.id)) {
      parent.children.push(item);
    }
  }

  return root.children;
}

export function findItem(items: TreeItem[], itemId: UniqueIdentifier) {
  return items.find(({id}) => id === itemId);
}

export function findItemDeep(
  items: TreeItems,
  itemId: UniqueIdentifier
): TreeItem | undefined {
  for (const item of items) {
    const {id, children} = item;

    if (id === itemId) {
      return item;
    }

    if (children.length) {
      const child = findItemDeep(children, itemId);

      if (child) {
        return child;
      }
    }
  }

  return undefined;
}

export function removeItem(items: TreeItems, id: UniqueIdentifier) {
  const newItems = [];

  for (const item of items) {
    if (item.id === id) {
      continue;
    }

    if (item.children.length) {
      item.children = removeItem(item.children, id);
    }

    newItems.push(item);
  }

  return newItems;
}

export function setProperty<T extends keyof TreeItem>(
  items: TreeItems,
  id: UniqueIdentifier,
  property: T,
  setter: (value: TreeItem[T]) => TreeItem[T]
) {
  console.log("About to enter for loop for setProperty: "+property);
  for (const item of items) {
    if (item.id === id) {
      console.log("ID: "+item.id+' '+property);
      item[property] = setter(item[property]);
      console.log("Item property: "+item[property]);
      break;
    }

    if (item.children.length) {
      // console.log("SEARCHING CHILDREN!!! ID: "+item.id+' '+property);
      item.children = setProperty(item.children, id, property, setter);
    }
  }

  return [...items];
}

export function setPropertyOnTree<T extends keyof TreeItem>(
  items: TreeItems,
  id: UniqueIdentifier,
  property: T,
  setter: (value: TreeItem[T]) => TreeItem[T],
  parentItem?: TreeItem
) {
  // console.log("About to enter setPropertyOnTree for loop: "+"ID: "+property);
  for (const item of items) {
    if (item.id === id) {

      item[property] = setter(item[property]);
      if(parentItem) {
        item['parentId'] = parentItem.id; //TODO: Hack!!! Pre-emptive setting of property. Flatten and rebuld the tree instead?
      }

      console.log("Item property: "+item[property]);
      if (item.children.length) {
        // console.log("About to set child properties: "+"ID: "+property);
        for (const child of item.children) {
          item.children = setPropertyOnTree(item.children, child.id, property, setter, item);
        }
      }

      break;
    }

    if (item.children.length) {
      // console.log("SEARCHING CHILDREN! in setPropertyOnTree!! ID: "+item.id+' '+property);
      item.children = setPropertyOnTree(item.children, id, property, setter, item);
    }
  }

  return [...items];
}

//TODO: This only works for boolean properties! It was developed for setting the 'goal' prop.
export function setGoalPropertyOnTree<T extends keyof TreeItem>(
  items: TreeItems,
  id: UniqueIdentifier,
  property: T,
  setter: (value: TreeItem[T]) => TreeItem[T],
  parentItem?: TreeItem
) {
  // console.log("About to enter setPropertyOnTree for loop: "+"ID: "+property);
  for (const item of items) {
    if (item.id === id) {
      // console.log("ID: "+item.id+' '+property);
      // console.log("Parent ID: "+parentItem.id+' '+property);
      if(parentItem && parentItem[property] === true) {
        console.log("Parent ID: "+parentItem.id+' '+property);
        //item.goal = parentItem.goal;
        item[property] = parentItem[property];
      }
      else{
        item[property] = setter(item[property]);
      }
      console.log("Item property: "+item[property]);
      if (item.children.length) {
        // console.log("About to set child properties: "+"ID: "+property);
        for (const child of item.children) {
          item.children = setGoalPropertyOnTree(item.children, child.id, property, setter, item);
        }
      }

      break;
    }

    if (item.children.length) {
      // console.log("SEARCHING CHILDREN! in setPropertyOnTree!! ID: "+item.id+' '+property);
      item.children = setGoalPropertyOnTree(item.children, id, property, setter, item);
    }
  }

  return [...items];
}

function countChildren(items: TreeItem[], count = 0): number {
  return items.reduce((acc, {children}) => {
    if (children.length) {
      return countChildren(children, acc + 1);
    }

    return acc + 1;
  }, count);
}

export function getChildCount(items: TreeItems, id: UniqueIdentifier) {
  const item = findItemDeep(items, id);

  return item ? countChildren(item.children) : 0;
}

export function removeChildrenOf(
  items: FlattenedItem[],
  ids: UniqueIdentifier[]
) {
  const excludeParentIds = [...ids];

  return items.filter((item) => {
    if (item.parentId && excludeParentIds.includes(item.parentId)) {
      if (item.children.length) {
        excludeParentIds.push(item.id);
      }
      return false;
    }

    return true;
  });
}

export function addTask( items,
                         setItems,
                         title,
                         primaryLabel = 'None',
                         status = 'New',
                         goal = false) { 

      //TODO: Graft into MyButton.js
      const cumulativeItemCount = parseInt(localStorage.getItem("itemCount"));
      let cumulativeItemCountString;
      console.log('Cumulative item count as parsed: '+cumulativeItemCount);
      if(isNaN(cumulativeItemCount)) {
        cumulativeItemCountString = "0";
      }
      else {
      cumulativeItemCountString = cumulativeItemCount;
      }

      if(items) {

        let newTaskTitle;

        if(title) {
          newTaskTitle = title;
        }
        else {
          newTaskTitle = prompt("Enter the title of the new task: ", "");
        }
        
        let newItem;

        newItem = [{id: uuid(), 
                    title: "Task #" + cumulativeItemCountString + " : " + newTaskTitle,
                    ownerId: getAuth().currentUser?.uid,
                    creationDate: new Date((new Date(Date.now())).toUTCString()),
                    children: [], 
                    goal: goal, 
                    status: status, 
                    primaryLabel: primaryLabel}];
      
        const newItems = newItem.concat(items);
        setItems(newItems);
      
        const dbOpenRequest = window.indexedDB.open("TasksDB",1);

        let db;

        let objectStore;

        dbOpenRequest.onerror = (event) => {
          console.error(`Database error: ${event.target.error?.message}`);
          alert("Issue connecting to DB!!");
        };

        dbOpenRequest.onsuccess = (event) => {
          db = event.target.result;
          console.log('DB connection opened for updating!!');

        // This assumes the objectStore has already been successfully created in the DB
        // TODO: handle the exception if not.
        const objectStore = db
          .transaction("TaskNestedLists", "readwrite")
          .objectStore("TaskNestedLists");
          
          console.log('Got object store on button clicked!! '+ objectStore);
          const storedItems = event.target.result;
          console.log('Items obtained from DB: '+storedItems);
          const requestUpdate = objectStore.put(newItems,1);
          requestUpdate.onsuccess = (event) => {
            // Success - the data is updated! Update the task count
            localStorage.setItem("itemCount", cumulativeItemCountString+1);
          };

        };

        dbOpenRequest.onupgradeneeded = (event) => {
          db = event.target.result;
          alert("DB was upgraded!!");  
          
          const objectStore = db
          .transaction("TaskNestedLists", "readwrite")
          .objectStore("TaskNestedLists");

          objectStore.put(newItems, 1);

        };

      }
    
    }
